import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

const SEO_API = '/seo-api';

interface PageSummary {
  id: string;
  title: string;
  slug: string;
  meta_description: string;
  category: string;
  generated_at: string;
}

const CATEGORY_LABELS: Record<string, { label: string; icon: string; desc: string }> = {
  'alcohol-addiction':              { label: 'Alcohol Addiction',       icon: '🍷', desc: 'Understanding and overcoming alcohol use disorder' },
  'opioid-dependency':              { label: 'Opioid & Opiate Addiction', icon: '💊', desc: 'Opioid addiction, withdrawal, and treatment options' },
  'general':                        { label: 'Recovery Resources',      icon: '🌱', desc: 'Guides, advice, and support for recovery' },
  'substance-specific-treatment':   { label: 'Substance Treatment',     icon: '🔬', desc: 'Treatment approaches for specific substances' },
};

const CATEGORY_DB_NAMES: Record<string, string> = {
  'alcohol-addiction':            'Alcohol Addiction',
  'opioid-dependency':            'Opioid Dependency',
  'general':                      'General',
  'substance-specific-treatment': 'Substance-Specific Treatment',
};

const SEOCategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const meta = CATEGORY_LABELS[category || ''] ?? { label: category ?? 'Resources', icon: '📋', desc: '' };
  const dbCategory = CATEGORY_DB_NAMES[category || ''] || category;

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    setError('');
    fetch(`${SEO_API}/pages?status=published&summary=true&limit=200&category=${encodeURIComponent(dbCategory!)}`)
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.pages || []);
        setPages(list);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load articles.'); setLoading(false); });
  }, [category, dbCategory]);

  const containerStyle: React.CSSProperties = {
    maxWidth: '1100px', margin: '0 auto', padding: '40px 20px',
  };

  return (
    <div style={containerStyle}>
      {/* Breadcrumb */}
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
        <Link to="/resources/" style={{ color: '#1a237e', textDecoration: 'none' }}>Resources</Link>
        {' › '}
        <span>{meta.label}</span>
      </div>

      {/* Header */}
      <div style={{ marginBottom: '36px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <span style={{ fontSize: '40px' }}>{meta.icon}</span>
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: 900, color: '#1a237e' }}>{meta.label}</h1>
        </div>
        {meta.desc && <p style={{ margin: 0, fontSize: '16px', color: '#555', maxWidth: '600px', lineHeight: 1.6 }}>{meta.desc}</p>}
        {!loading && <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#888' }}>{pages.length} article{pages.length !== 1 ? 's' : ''}</p>}
      </div>

      {loading ? (
        <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>Loading articles…</p>
      ) : error ? (
        <p style={{ color: '#c62828' }}>{error}</p>
      ) : pages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>
          <p>No articles in this category yet.</p>
          <Link to="/resources/" style={{ color: '#1a237e' }}>← All Resources</Link>
        </div>
      ) : (
        <>
          {/* Featured first article */}
          <a
            href={`/resources/${pages[0].slug}`}
            style={{
              display: 'block', background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
              borderRadius: '12px', padding: '28px 32px', marginBottom: '20px',
              textDecoration: 'none', color: '#fff',
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, marginBottom: '10px' }}>
              Featured Article
            </div>
            <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: 800, lineHeight: 1.35 }}>{pages[0].title}</h2>
            {pages[0].meta_description && (
              <p style={{ margin: 0, opacity: 0.85, fontSize: '14px', lineHeight: 1.6 }}>{pages[0].meta_description}</p>
            )}
            <div style={{ marginTop: '16px', fontSize: '13px', fontWeight: 700, color: '#90caf9' }}>Read article →</div>
          </a>

          {/* Grid of remaining articles */}
          {pages.length > 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
              {pages.slice(1).map(p => (
                <a
                  key={p.id}
                  href={`/resources/${p.slug}`}
                  style={{
                    display: 'block', background: '#fff', borderRadius: '10px',
                    padding: '18px 20px', textDecoration: 'none', color: 'inherit',
                    border: '1px solid #e8eaf6', borderLeft: '4px solid #1a237e',
                  }}
                >
                  <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 700, color: '#1a237e', lineHeight: 1.35 }}>{p.title}</h3>
                  {p.meta_description && (
                    <p style={{ margin: 0, fontSize: '13px', color: '#666', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {p.meta_description}
                    </p>
                  )}
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SEOCategoryPage;
